// Carrier-stage derivation — closes the §13 cross-system dependency (documented,
// detect() unchanged).
//
// System 1 `detect()` publishes the carrier CURVE (`redLine.carrier`, per-stage)
// and its MEMBERS (`redLine.members`, layer names) but NOT the indices of the
// carrier STAGES. System 2 `selectQuotes` needs those indices for §IV/s7 evidence
// and to flag which stages get the `carrier_evidence_prompt` (isCarrier); §II/§IV/§V
// and wizard s5/s6 draw the same list as the evidence line (D&R s486.d1). Quote-spec
// §13 says: "System 1 publishes indices OR a documented derivation." This is that
// documented derivation, implemented per detection-spec §3.5 (Evidence stages):
//
//   together (pair/triplet)      — whole range
//   divergence / late-conv       — start + gap-reversal point + current
//   singleton                    — stages where |gap(L)| ≥ SINGLETON_GAP
//   singleton-cycling            — all extrema of the carrier line
//   v-shape-recovery             — trough + return
//   field-level-recovery         — common trough + return
//   scatter                      — none (no single carrier; §IV still renders — names no single line)
//
// Lives in src/ since s489 (Эпик 11): it was a dataset-harness file, but the live
// Passport needs the same indices, and `buildPassport` must reproduce the frozen
// datasets byte-for-byte. The harness now imports THIS module — one source of truth.
//
// NOT to be confused with `cards.js:evidenceStages`, which is the per-CARD evidence
// of a single detection (§V card line). Family-level carrier stages and card-level
// evidence agree only by accident; the CD §V boards (carrierStages [2,3,4] on v01)
// are canon for THIS derivation.

import { THRESHOLDS as T } from './thresholds.js';
import { normalizeStages, gapAt } from './input.js';

/**
 * @param {object} det   result of detect()
 * @param {Array}  sliderStages  the same wizard slider input fed to detect()
 * @returns {{ carrierStages: number[], derivation: string }}
 */
export function deriveCarrierStages(det, sliderStages) {
  const out = pick(det, sliderStages);
  // s950.d1 (Н42): a stage where the carrier has no answer is not evidence. Every branch below
  // reads the curve or the slider defaults without the per-stage mask — an untouched layer sits
  // at 0 in `values` (input.js), and the ends 0 / N−1 are taken unconditionally — so the stage
  // where nothing was answered got counted, numbered, carded and sent to the judge. Dropped AFTER
  // the pick, never replaced by the nearest answered stage: same as the canon board
  // (`Life Map - s5 Red Line v7.html` evidenceIdx() :405–407).
  const curve = det.redLine.carrier;
  if (!Array.isArray(curve)) return out;
  return { ...out, carrierStages: out.carrierStages.filter((i) => curve[i] != null) };
}

// ⭐ ВЫБОР ПО ОТВЕЧЕННОЙ СЕРИИ (`s958.d1`, Н42). Каждая ветка считает на сжатой серии этапов, где
// у несущей есть ответ, и возвращает индексы в ИСХОДНОЙ нумерации: «начало» = первый отвеченный,
// «сейчас» = последний отвеченный, дно / экстремумы / разворот разрыва — только среди отвеченных.
// Это та же серия, что у THE SHAPE (`answered()`, `s501` вариант A), а не подстановка ближайшего.
// Ветки со вторым слоем (пара opposition, group-vs-group) считают на этапах, где отвечены ОБЕ
// стороны. Не хватило таких этапов — список пуст, и это стоп по `s958.d1`, а не хвост.
// Строки `derivation` НЕ менялись: они заморожены в датасетах (`passportData.test.js`), а на
// всех-тронутых входах отвеченная серия = весь диапазон — выбор совпадает с прежним байт в байт.
function pick(det, sliderStages) {
  const { stages, active } = normalizeStages(sliderStages);
  const N = stages.length;
  const all = [...Array(N).keys()];
  const { family, type, layers: pairLayers, blocks } = det.mainPattern;
  const members = det.redLine.members;
  const curve = det.redLine.carrier;

  const clean = (a) =>
    [...new Set(a)].filter((i) => Number.isInteger(i) && i >= 0 && i < N).sort((x, y) => x - y);

  // отвеченная серия: исходные индексы и значения несущей на них
  const answered = Array.isArray(curve) ? all.filter((i) => curve[i] != null) : all.slice();
  const series = answered.map((i) => (Array.isArray(curve) ? curve[i] : 0));
  const last = answered[answered.length - 1];

  // local extrema (endpoints + interior turning points) of a numeric series — позиции В СЕРИИ
  const extrema = (c) => {
    if (c.length === 0) return [];
    const e = [0, c.length - 1];
    for (let i = 1; i < c.length - 1; i++) {
      if ((c[i] >= c[i - 1] && c[i] >= c[i + 1]) || (c[i] <= c[i - 1] && c[i] <= c[i + 1])) e.push(i);
    }
    return e;
  };
  const argmin = (c) => c.reduce((m, v, i) => (v < c[m] ? i : m), 0);
  const touchedAt = (i, L) => Boolean(stages[i].touched[L]);

  switch (family) {
    case 'singleton': {
      const L = members[0];
      if (type === 'singleton-cycling') {
        return {
          carrierStages: clean(extrema(series).map((k) => answered[k])),
          derivation: `singleton-cycling · all extrema of ${L}`,
        };
      }
      if (type === 'v-shape-recovery') {
        const tr = answered.length ? answered[argmin(series)] : undefined;
        return { carrierStages: clean([tr, last]), derivation: `v-shape-recovery · trough(@${tr}) + return` };
      }
      const hit = answered.filter((i) => Math.abs(gapAt(stages, active, L, i)) >= T.SINGLETON_GAP);
      return {
        carrierStages: clean(hit.length ? hit : [last]),
        derivation: `${type} · stages |gap(${L})|≥${T.SINGLETON_GAP}`,
      };
    }

    case 'opposition': {
      if (type === 'group-vs-group') {
        const both = Array.isArray(blocks) && blocks.length === 2
          ? all.filter((i) => blocks.every((b) => b.some((L) => touchedAt(i, L))))
          : answered;
        return {
          carrierStages: clean([both[0], both[both.length - 1]]),
          derivation: 'group-vs-group · start + current (block divergence)',
        };
      }
      // pair divergence / late-convergence / anti-correlation → start + reversal + current,
      // на этапах, где отвечены несущая и второй слой пары
      const L = members[0];
      const other = Array.isArray(pairLayers) ? pairLayers.find((o) => o !== L) : undefined;
      const both = other ? answered.filter((i) => touchedAt(i, other)) : answered;
      if (both.length === 0) {
        return { carrierStages: [], derivation: `${type} · no stage with both ${L} and ${other} answered` };
      }
      const gaps = both.map((i) => gapAt(stages, active, L, i));
      let rev = both[both.length - 1];
      let best = -Infinity;
      for (let k = 1; k < both.length; k++) {
        const d = Math.abs(gaps[k] - gaps[k - 1]);
        if (d > best) {
          best = d;
          rev = both[k];
        }
      }
      return {
        carrierStages: clean([both[0], rev, both[both.length - 1]]),
        derivation: `${type} · start + gap-reversal(@${rev}) + current`,
      };
    }

    case 'field': {
      const tr = answered.length ? answered[argmin(series)] : undefined;
      return { carrierStages: clean([tr, last]), derivation: `field-level-recovery · trough(@${tr}) + return` };
    }

    case 'together':
      return { carrierStages: answered.slice(), derivation: 'together · whole range (§3.5)' };

    case 'scatter':
    default:
      return { carrierStages: [], derivation: 'scatter · no single carrier (§2.9)' };
  }
}
